import React, { useCallback, useContext, useMemo, useState } from 'react';
import GravTitelKort from '../GravTitelKort';
import ObjektVySida from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVySida/ObjektVySida';
import ObjektVyKolumner, {
  ObjektVyKolumn,
} from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKolumner/ObjektVyKolumner';
import { ObjektVyKort } from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKort/ObjektVyKort';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import EDCaptionedValue from '../../../../../../BaseComponents/CaptionedValue/EDCaptionedValue';
import { EDControlledExpansionPanel } from '../../../../../../BaseComponents/Accordion/EDExpansionPanel';
import AttributeDisplayText from '../../../../../../Components/Attributes/AttributeDisplayText/AttributeDisplayText';
import { GravplatsQuery_node_TEDGAGrav as IGravplatsNode } from '../Queries/_QueryTypes/GravplatsQuery';
import clientAvenyModel from '../../../../../../Model/clientAvenyModel';
import { getAttributeInfo } from '../../../../../../Model/avenyModelUtils';
import { ModelContext } from '../../../../../../Model/ModelContext/ModelContext';
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';
import { getMaskedFromCurrentValues } from '../../../../_Common/Hooks/useAttributeValues';

const GravModel = clientAvenyModel.GA.BusinessModels.GAGrav;

interface IKontakterProps {
  sidNr: number;
  nodeData: IGravplatsNode | null | undefined;
  fullPage: boolean;
}

export const Kontakter: React.FC<IKontakterProps> = ({
  sidNr,
  nodeData,
  fullPage,
}) => {
  const { avenyCollectionMap } = useContext(ModelContext);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const onChange = useCallback((itemId: string) => {
    setExpanded(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  const attrInfoFranDatum = useMemo(
    () =>
      getAttributeInfo(GravModel, 'Kontakter.FRANDATUM', avenyCollectionMap),
    [avenyCollectionMap]
  );

  const attrInfoTelefon1 = useMemo(
    () =>
      getAttributeInfo(
        GravModel,
        'Kontakter.OrgPers.Adress.TELEFON1',
        avenyCollectionMap
      ),
    [avenyCollectionMap]
  );

  const attrInfoTelefonMobil = useMemo(
    () =>
      getAttributeInfo(
        GravModel,
        'Kontakter.OrgPers.Adress.TELEFON_MOBIL',
        avenyCollectionMap
      ),
    [avenyCollectionMap]
  );

  const attrInfoSmtp1 = useMemo(
    () =>
      getAttributeInfo(
        GravModel,
        'Kontakter.OrgPers.Adress.SMTP1',
        avenyCollectionMap
      ),
    [avenyCollectionMap]
  );

  const attrInfoKompaktAdress = useMemo(
    () =>
      getAttributeInfo(
        GravModel,
        'Kontakter.OrgPers.Adress.KompaktAdress',
        avenyCollectionMap
      ),
    [avenyCollectionMap]
  );

  const attrInfoAvtalsperiod = useMemo(
    () =>
      getAttributeInfo(
        GravModel,
        'Kontakter.Kund.Avtalsperiod',
        avenyCollectionMap
      ),
    [avenyCollectionMap]
  );

  const contactsByType = useMemo(() => {
    if (!nodeData?._Associations?.Kontakter_All)
      return {
        gravrattshavare: [],
        betalningsansvariga: [],
        ovriga: [],
        dodsbo: [],
      };

    const gravrattshavare: any[] = [];
    const betalningsansvariga: any[] = [];
    const ovriga: any[] = [];
    const dodsbo: any[] = [];

    nodeData._Associations.Kontakter_All.forEach(item => {
      if (item?._typ === 'GRAVRATTSINNEHAVARE') {
        gravrattshavare.push(item);
      } else if (item?._typ === 'BETALNINGSANSVARIG') {
        betalningsansvariga.push(item);
      } else if (item?._typ === 'DODSBO') {
        dodsbo.push(item);
      } else {
        ovriga.push(item);
      }
    });

    return { gravrattshavare, betalningsansvariga, ovriga, dodsbo };
  }, [nodeData]);

  const renderContactCard = (item: any, index: number) => {
    const itemKey = item?.id ?? `contact-${index}`;
    const isExpanded = expanded.has(itemKey);
    const orgPers = item?._Associations?.OrgPers;
    const adress = orgPers?._Associations?.Adress;
    const phone = adress?.TELEFON_MOBIL || adress?.TELEFON1;
    const kund = item?._Associations?.Kund;
    const adress2 = (() => {
      const co = adress?.CO;

      if (co) {
        const parts: string[] = [`c/o ${co}`];
        if (adress?.ADRESS) parts.push(adress.ADRESS);
        if (adress?.POSTNR || adress?.ORT) {
          const postalLine = [adress?.POSTNR, adress?.ORT]
            .filter(Boolean)
            .join('  ');
          if (postalLine) parts.push(postalLine);
        }
        return parts.join('\n');
      }
      return adress?.KompaktAdress || adress?.ADRESS || kund?.KompaktAdress;
    })();

    return (
      <ObjektVyKort
        key={itemKey}
        className="position-relative"
        backgroundColor={'white'}
        cardInCard
        noPadding
      >
        <EDControlledExpansionPanel
          expanded={isExpanded}
          onChange={() => onChange(itemKey)}
          overflowHidden
          label={
            item?._typ === 'BETALNINGSANSVARIG' &&
            item?.KontaktTypLong &&
            item?.IDString ? (
              <EDBox
                p={2}
                width={'100%'}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 0, sm: 2 },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                }}
              >
                <EDTypography variant="h6" color={'black'} noWrap>
                  {item.KontaktTypLong}
                </EDTypography>
                <EDTypography variant="h6" color={'black'} noWrap>
                  {item.IDString}
                </EDTypography>
              </EDBox>
            ) : (
              <EDTypography variant="h6" color={'black'} width={'100%'} p={2}>
                {item?._typ === 'DODSBO'
                  ? item?.IDString || 'Dödsbo'
                  : orgPers?.NamnFE ||
                    item?.KontaktTypLong ||
                    item?.IDString ||
                    '-'}
              </EDTypography>
            )
          }
        >
          {/* Gravrättsinnehavare */}
          {item?._typ === 'GRAVRATTSINNEHAVARE' && orgPers && (
            <>
              {adress2 && (
                <EDCaptionedValue
                  label="Adress"
                  name={`${attrInfoKompaktAdress.attribute.AttributeName}_${index}`}
                  size="large"
                >
                  <AttributeDisplayText
                    value={adress2}
                    attributeModel={attrInfoKompaktAdress.attribute}
                    collection={GravModel}
                    forceText
                    masked={getMaskedFromCurrentValues(
                      item,
                      'OrgPers.Adress.KompaktAdress',
                      attrInfoKompaktAdress.attribute
                    )}
                  />
                </EDCaptionedValue>
              )}

              {phone && (
                <EDCaptionedValue
                  label="Telefon"
                  name={`telefon_${index}`}
                  size="large"
                >
                  <AttributeDisplayText
                    value={phone}
                    attributeModel={
                      adress?.TELEFON_MOBIL
                        ? attrInfoTelefonMobil.attribute
                        : attrInfoTelefon1.attribute
                    }
                    collection={GravModel}
                    forceText
                    masked={getMaskedFromCurrentValues(
                      item,
                      adress?.TELEFON_MOBIL
                        ? 'OrgPers.Adress.TELEFON_MOBIL'
                        : 'OrgPers.Adress.TELEFON1',
                      adress?.TELEFON_MOBIL
                        ? attrInfoTelefonMobil.attribute
                        : attrInfoTelefon1.attribute
                    )}
                  />
                </EDCaptionedValue>
              )}

              {adress?.SMTP1 && (
                <EDCaptionedValue
                  label="E-post"
                  name={`${attrInfoSmtp1.attribute.AttributeName}_${index}`}
                  size="large"
                >
                  <AttributeDisplayText
                    value={adress.SMTP1}
                    attributeModel={attrInfoSmtp1.attribute}
                    collection={GravModel}
                    forceText
                    masked={getMaskedFromCurrentValues(
                      item,
                      'OrgPers.Adress.SMTP1',
                      attrInfoSmtp1.attribute
                    )}
                  />
                </EDCaptionedValue>
              )}

              <EDCaptionedValue
                label="Registrerad"
                name={`${attrInfoFranDatum.attribute.AttributeName}_${index}`}
                size="large"
              >
                <AttributeDisplayText
                  value={item?.FRANDATUM}
                  attributeModel={attrInfoFranDatum.attribute}
                  collection={GravModel}
                  forceText
                  masked={getMaskedFromCurrentValues(
                    item,
                    'FRANDATUM',
                    attrInfoFranDatum.attribute
                  )}
                />
              </EDCaptionedValue>
            </>
          )}

          {/* Övriga kontakter */}
          {item?._typ === 'KONTAKTOVRIG' && orgPers && (
            <>
              {adress2 && (
                <EDCaptionedValue
                  label="Adress"
                  name={`${attrInfoKompaktAdress.attribute.AttributeName}_${index}`}
                  size="large"
                >
                  <AttributeDisplayText
                    value={adress2}
                    attributeModel={attrInfoKompaktAdress.attribute}
                    collection={GravModel}
                    forceText
                    masked={getMaskedFromCurrentValues(
                      item,
                      'OrgPers.Adress.KompaktAdress',
                      attrInfoKompaktAdress.attribute
                    )}
                  />
                </EDCaptionedValue>
              )}

              {phone && (
                <EDCaptionedValue
                  label="Telefon"
                  name={`telefon_${index}`}
                  size="large"
                >
                  <AttributeDisplayText
                    value={phone}
                    attributeModel={
                      adress?.TELEFON_MOBIL
                        ? attrInfoTelefonMobil.attribute
                        : attrInfoTelefon1.attribute
                    }
                    collection={GravModel}
                    forceText
                    masked={getMaskedFromCurrentValues(
                      item,
                      adress?.TELEFON_MOBIL
                        ? 'OrgPers.Adress.TELEFON_MOBIL'
                        : 'OrgPers.Adress.TELEFON1',
                      adress?.TELEFON_MOBIL
                        ? attrInfoTelefonMobil.attribute
                        : attrInfoTelefon1.attribute
                    )}
                  />
                </EDCaptionedValue>
              )}

              {adress?.SMTP1 && (
                <EDCaptionedValue
                  label="E-post"
                  name={`${attrInfoSmtp1.attribute.AttributeName}_${index}`}
                  size="large"
                >
                  <AttributeDisplayText
                    value={adress.SMTP1}
                    attributeModel={attrInfoSmtp1.attribute}
                    collection={GravModel}
                    forceText
                    masked={getMaskedFromCurrentValues(
                      item,
                      'OrgPers.Adress.SMTP1',
                      attrInfoSmtp1.attribute
                    )}
                  />
                </EDCaptionedValue>
              )}
            </>
          )}

          {/* Betalningsansvarig */}
          {item?._typ === 'BETALNINGSANSVARIG' && (
            <>
              {adress2 && (
                <EDCaptionedValue
                  label="Adress"
                  name={`${attrInfoKompaktAdress.attribute.AttributeName}_${index}`}
                  size="large"
                >
                  <AttributeDisplayText
                    value={adress2}
                    attributeModel={attrInfoKompaktAdress.attribute}
                    collection={GravModel}
                    forceText
                    masked={getMaskedFromCurrentValues(
                      item,
                      'OrgPers.Adress.KompaktAdress',
                      attrInfoKompaktAdress.attribute
                    )}
                  />
                </EDCaptionedValue>
              )}

              {phone && (
                <EDCaptionedValue
                  label="Telefon"
                  name={`telefon_${index}`}
                  size="large"
                >
                  <AttributeDisplayText
                    value={phone}
                    attributeModel={
                      adress?.TELEFON_MOBIL
                        ? attrInfoTelefonMobil.attribute
                        : attrInfoTelefon1.attribute
                    }
                    collection={GravModel}
                    forceText
                    masked={getMaskedFromCurrentValues(
                      item,
                      adress?.TELEFON_MOBIL
                        ? 'OrgPers.Adress.TELEFON_MOBIL'
                        : 'OrgPers.Adress.TELEFON1',
                      adress?.TELEFON_MOBIL
                        ? attrInfoTelefonMobil.attribute
                        : attrInfoTelefon1.attribute
                    )}
                  />
                </EDCaptionedValue>
              )}

              {item?.KontaktTypShort !== 'Fakt.kund' && (
                <EDCaptionedValue
                  label="Avtalsperiod"
                  name={`${attrInfoAvtalsperiod.attribute.AttributeName}_${index}`}
                  size="large"
                >
                  <AttributeDisplayText
                    value={kund?.Avtalsperiod}
                    attributeModel={attrInfoAvtalsperiod.attribute}
                    collection={GravModel}
                    forceText
                    masked={getMaskedFromCurrentValues(
                      item,
                      'Kund.Avtalsperiod',
                      attrInfoAvtalsperiod.attribute
                    )}
                  />
                </EDCaptionedValue>
              )}
            </>
          )}

          {/* Dödsbo */}
          {item?._typ === 'DODSBO' && (
            <EDCaptionedValue
              label="Dödsbo"
              name={`dodsbo_${index}`}
              size="large"
              hideLabel
            >
              <EDTypography>
                Dödsbo efter {item?.IDString || 'okänd'}
              </EDTypography>
            </EDCaptionedValue>
          )}
        </EDControlledExpansionPanel>
      </ObjektVyKort>
    );
  };

  return (
    <ObjektVySida
      sidNr={sidNr}
      caption={'Kontakter'}
      collectionName={GravModel.CollectionName}
    >
      <ObjektVyKolumner>
        <ObjektVyKolumn fullPage={fullPage}>
          <GravTitelKort nodeData={nodeData} />

          {/* Visa bara kort om kontakter finns */}
          {contactsByType.gravrattshavare.length > 0 && (
            <ObjektVyKort
              caption="Gravrättsinnehavare"
              className="position-relative"
              backgroundColor={'white'}
            >
              {contactsByType.gravrattshavare.map((item, index) =>
                renderContactCard(item, index)
              )}
            </ObjektVyKort>
          )}

          {contactsByType.betalningsansvariga.length > 0 && (
            <ObjektVyKort
              caption="Betalningsansvarig"
              className="position-relative"
              backgroundColor={'white'}
            >
              {contactsByType.betalningsansvariga.map((item, index) =>
                renderContactCard(item, index)
              )}
            </ObjektVyKort>
          )}

          {contactsByType.ovriga.length > 0 && (
            <ObjektVyKort
              caption="Övriga kontakter"
              className="position-relative"
              backgroundColor={'white'}
            >
              {contactsByType.ovriga.map((item, index) =>
                renderContactCard(item, index)
              )}
            </ObjektVyKort>
          )}

          {contactsByType.dodsbo.length > 0 && (
            <ObjektVyKort
              caption="Dödsbo"
              className="position-relative"
              backgroundColor={'white'}
            >
              {contactsByType.dodsbo.map((item, index) =>
                renderContactCard(item, index)
              )}
            </ObjektVyKort>
          )}
        </ObjektVyKolumn>
      </ObjektVyKolumner>
    </ObjektVySida>
  );
};

export default Kontakter;
